import { useCallback, useEffect, useRef } from 'react';
import {
  serializeForClipboard,
  isValidFokusClipboard,
  cloneTaskFromClipboard,
  remapDependencies,
  attachNestedSprintsFromClipboard,
  collectNestedSprintData,
  generateCustomFieldIds,
} from '../../shared/clipboardUtils';
import { shouldDeferToNativeClipboardShortcuts } from '../../FlowchartView/hooks/utils/overlayPointerGuard';

/**
 * useKanbanClipboard Hook
 * 
 * Handles copy/paste/duplicate operations for Kanban cards using system clipboard.
 * Uses the same JSON format as FlowchartView for cross-view compatibility.
 * 
 * - Ctrl+C copies selected cards to system clipboard as JSON
 * - Ctrl+V pastes from system clipboard (if valid Fokus format)
 * - Ctrl+D duplicates selected cards
 * - Delete/Backspace deletes selected cards
 * - Ctrl+A selects all cards
 */
export function useKanbanClipboard({
  tasks,
  selectedTaskIds,
  setSelectedTaskIds,
  nestedSprints = [],
  allSprints = [],
  allTags = [],
  customFields = [],
  onPasteTasks,
  onDeleteTasks,
  scrollToTask, // Optional callback to scroll to a task after paste
}) {
  // Keep a local ref for duplicate (copy+paste in same action)
  const lastCopiedRef = useRef(null);

  /**
   * Copy selected cards to system clipboard
   */
  const copySelectedCards = useCallback(async () => {
    if (selectedTaskIds.size === 0) return false;
    
    const selectedIds = Array.from(selectedTaskIds);
    const selectedTasks = tasks.filter(t => selectedIds.includes(t.id));
    
    if (selectedTasks.length === 0) return false;
    
    // For Kanban, we don't have meaningful positions, so use simple relative positions
    const relativePositions = {};
    selectedTasks.forEach((task, idx) => {
      relativePositions[task.id] = { x: 0, y: idx * 50 }; // Stack vertically
    });
    
    // Collect nested sprint data
    const nestedSprintData = collectNestedSprintData(selectedTasks, nestedSprints, allSprints);
    
    // Serialize for clipboard (include tag definitions for color preservation, custom fields)
    const clipboardData = serializeForClipboard(selectedTasks, relativePositions, nestedSprintData, allTags, customFields);
    
    // Store locally for duplicate action
    lastCopiedRef.current = clipboardData;
    
    // Write to system clipboard
    try {
      await navigator.clipboard.writeText(JSON.stringify(clipboardData, null, 2));
      console.log(`📋 Copied ${selectedTasks.length} card(s) to clipboard (JSON format)`);
      return true;
    } catch (error) {
      console.error('Failed to write to clipboard:', error);
      return true; // Still stored locally
    }
  }, [selectedTaskIds, tasks, nestedSprints, allTags, customFields]);

  /**
   * Paste cards from system clipboard
   */
  const pasteCards = useCallback(async (clipboardDataOverride = null) => {
    if (!onPasteTasks) return false;
    
    let clipboardData = clipboardDataOverride;
    
    // Try to read from system clipboard if no override provided
    if (!clipboardData) {
      try {
        const text = await navigator.clipboard.readText();
        clipboardData = JSON.parse(text);
      } catch (error) {
        // Fallback to local ref (for duplicate action or clipboard access denied)
        clipboardData = lastCopiedRef.current;
      }
    }
    
    // Validate clipboard data
    if (!isValidFokusClipboard(clipboardData)) {
      console.log('📋 Clipboard does not contain valid Fokus data');
      return false;
    }
    
    const { tasks: clipboardTasks, nestedSprints: clipboardNestedSprints, tagDefinitions = [], customFieldDefinitions = [] } = clipboardData;
    
    const timestamp = Date.now();
    const idMap = new Map();
    const optionIdMap = new Map();
    
    // Generate new IDs for custom field definitions (v2 uses indices)
    const { 
      definitions: newCustomFieldDefs, 
      fieldIdMap: customFieldIdMap, 
      optionIdMap: customFieldOptionIdMap 
    } = generateCustomFieldIds(customFieldDefinitions, timestamp);
    
    // Clone all tasks with custom field ID remapping
    const clonedTasks = clipboardTasks.map((task, idx) => 
      cloneTaskFromClipboard(task, idMap, optionIdMap, timestamp, idx, customFieldIdMap, customFieldOptionIdMap)
    );
    
    // Remap dependencies to new IDs
    remapDependencies(clonedTasks, idMap, optionIdMap);
    
    // For Kanban, positions don't matter much - tasks go into their status columns
    const pastedPositions = {};
    clonedTasks.forEach(task => {
      pastedPositions[task.id] = { x: 0, y: 0 };
    });
    
    const clonedNestedSprints = attachNestedSprintsFromClipboard(
      clonedTasks,
      clipboardNestedSprints,
      timestamp,
    );
    clonedTasks.forEach(task => {
      delete task._clipboardPos;
    });
    
    console.log(`📋 Pasting ${clonedTasks.length} card(s)`);
    
    // Call the paste handler (include tag definitions for color preservation, custom field definitions with new IDs)
    onPasteTasks({
      tasks: clonedTasks,
      positions: pastedPositions,
      nestedSprints: clonedNestedSprints,
      tagDefinitions,
      customFieldDefinitions: newCustomFieldDefs,
    });
    
    // Select the pasted cards
    const newIds = clonedTasks.map(t => t.id);
    if (setSelectedTaskIds) {
      setSelectedTaskIds(new Set(newIds));
    }
    
    // Scroll to the first pasted card after a brief delay
    if (scrollToTask && newIds.length > 0) {
      setTimeout(() => {
        scrollToTask(newIds[0]);
      }, 100);
    }
    
    return true;
  }, [onPasteTasks, setSelectedTaskIds, scrollToTask]);

  /**
   * Copy a specific card to clipboard (for menu action)
   */
  const copyCard = useCallback(async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return false;
    
    const nestedSprintData = collectNestedSprintData([task], nestedSprints, allSprints);
    // Include tag definitions for color preservation
    const clipboardData = serializeForClipboard([task], { [taskId]: { x: 0, y: 0 } }, nestedSprintData, allTags);
    
    lastCopiedRef.current = clipboardData;
    
    try {
      await navigator.clipboard.writeText(JSON.stringify(clipboardData, null, 2));
      console.log(`📋 Copied "${task.name}" to clipboard (JSON format)`);
      return true;
    } catch (error) {
      console.error('Failed to write to clipboard:', error);
      return true;
    }
  }, [tasks, nestedSprints, allTags]);

  /**
   * Duplicate a specific card (for menu action)
   */
  const duplicateCard = useCallback(async (taskId) => {
    if (await copyCard(taskId)) {
      await pasteCards(lastCopiedRef.current);
    }
  }, [copyCard, pasteCards]);

  /**
   * Duplicate selected cards (copy + paste in one action)
   */
  const duplicateSelectedCards = useCallback(async () => {
    if (await copySelectedCards()) {
      await pasteCards(lastCopiedRef.current);
    }
  }, [copySelectedCards, pasteCards]);

  /**
   * Delete selected cards
   */
  const deleteSelectedCards = useCallback(() => {
    if (selectedTaskIds.size === 0 || !onDeleteTasks) return;
    
    const idsToDelete = Array.from(selectedTaskIds);
    console.log(`🗑️ Deleting ${idsToDelete.length} card(s)`);
    
    onDeleteTasks(idsToDelete);
    setSelectedTaskIds(new Set());
  }, [selectedTaskIds, onDeleteTasks, setSelectedTaskIds]);

  /**
   * Keyboard shortcut handler
   */
  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (e.repeat) return;
      if (shouldDeferToNativeClipboardShortcuts(e)) {
        return;
      }
      
      // Ctrl+C or Cmd+C - Copy
      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedTaskIds.size > 0) {
          e.preventDefault();
          await copySelectedCards();
        }
      }
      
      // Ctrl+X or Cmd+X - Cut (copy then delete)
      if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
        if (selectedTaskIds.size > 0) {
          e.preventDefault();
          const copied = await copySelectedCards();
          if (copied && onDeleteTasks) {
            const idsToDelete = Array.from(selectedTaskIds);
            onDeleteTasks(idsToDelete);
            setSelectedTaskIds(new Set());
          }
        }
      }
      
      // Ctrl+V or Cmd+V - Paste
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        await pasteCards();
      }
      
      // Ctrl+D or Cmd+D - Duplicate
      if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
        if (selectedTaskIds.size > 0) {
          e.preventDefault();
          await duplicateSelectedCards();
        }
      }
      
      // Delete or Backspace - Delete selected
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedTaskIds.size > 0) {
        e.preventDefault();
        deleteSelectedCards();
      }
      
      // Escape - Clear selection
      if (e.key === 'Escape') {
        setSelectedTaskIds(new Set());
      }
      
      // Ctrl+A or Cmd+A - Select all
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        setSelectedTaskIds(new Set(tasks.map(t => t.id)));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedTaskIds, tasks, copySelectedCards, pasteCards, duplicateSelectedCards, deleteSelectedCards, setSelectedTaskIds, onDeleteTasks]);

  /**
   * Check if clipboard has valid Fokus data
   */
  const checkClipboardData = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      const data = JSON.parse(text);
      return isValidFokusClipboard(data);
    } catch {
      return !!lastCopiedRef.current;
    }
  }, []);

  return {
    copySelectedCards,
    copyCard,
    pasteCards,
    duplicateCard,
    duplicateSelectedCards,
    deleteSelectedCards,
    hasClipboardData: checkClipboardData,
  };
}

export default useKanbanClipboard;
